import { getPetIndex } from "@/modules/Pet/helpers/helpers"
import { Breed, Meta, Owner, Pet } from "@/shared/types/global-store"
import { CreateQuoteResponse, PricingResponse, SyncBackBody } from "@/shared/types/lead-api-types"
import { generatePetId, generateUserId } from "@/shared/utils/helpers"
import { assign } from "@xstate/immer"
import * as R from "ramda"
import { createMachine, interpret } from "xstate"

// Opt-in only: set NEXT_PUBLIC_XSTATE_INSPECT=true in .env.local to enable.
// Off by default so dev sessions and Playwright don't get a stately.ai popup.
if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_XSTATE_INSPECT === "true") {
  void import("@xstate/inspect").then(({ inspect }) => {
    inspect({ iframe: false })
  })
}

export type Marketing = {
  referrer_details?: string
  referrer_type?: string
  shop?: string
}

export type SubscriptionFlowContext = {
  pets: Pet[]
  currentPetId?: string
  owner: Owner
  meta: Meta
  pricing?: PricingResponse
  marketing: Marketing
  step: number
  setLoadingPrepackage: boolean
  rcContractId?: string
  prepackageVersion?: "A" | "B" | null
}

export type NewSubscriptionFlowContext = {
  quote: Partial<SyncBackBody>
  currentPetIdx: number | null
  rc_contract_id?: string
  risk_carrier?: string
  pricing_version?: string
  currentPetId: string | null
  nextPetIndex: number
  setLoadingPrepackage: boolean
  stripeClientSecret: string | null
}

type AddPetNameEvent = {
  type: "ADD_PET_NAME"
  petName: string
  petId?: string | null
}

type UpdateCurrentPetIdx = {
  type: "UPDATE_CURRENT_PET_IDX"
  petIndex: number
  petId?: string | null
}

type AddPetInfosEvent = {
  type: "ADD_PET_INFORMATIONS"
  petGender?: Pet["sexe"]
  petType?: Pet["type"]
  petId: string
}

type AddPetBirthDateEvent = { type: "ADD_PET_BIRTH_DATE"; petBirthDate: string; petIndex: number }

export type AddPetBreedEvent = {
  type: "ADD_PET_BREED"
  petBreed: Partial<Breed>
  petIndex: number
}

export type SetQuoteFromBackendEvent = {
  type: "SET_QUOTE_FROM_BACKEND"
  quoteResponse: CreateQuoteResponse
}

export type AddAttributionQuestionEvent = {
  type: "ADD_ATTRIBUTION_QUESTION"
  referrer_details?: string
  referrer_type?: string
  shop?: string
}

export type AddOwnerFirstInformations = {
  type: "ADD_OWNER_FIRST_INFORMATIONS"
  email: string
  rc_contract_id: string
  risk_carrier: string
  pricing_version: string
  phone: string
}

export type UpdateQuoteEvent = {
  type: "UPDATE_QUOTE"
  quote: Partial<SyncBackBody>
}

export type SetCurrentPetIdx = {
  type: "SET_CURRENT_PET_IDX"
  petIndex: number | null
  petId: string | null
}

export type AddNewPet = {
  type: "ADD_NEW_PET"
}

export type StopLoadingPrepackage = {
  type: "STOP_LOADING_PREPACKAGE"
}

export type StartLoadingPrepackage = {
  type: "START_LOADING_PREPACKAGE"
}

export type SetStripeClientSecretEvent = {
  type: "SET_STRIPE_CLIENT_SECRET"
  clientSecret: string | null
}

export type SetEmailSentToEvent = {
  type: "SET_EMAIL_SENT_TO"
  email: string
}

export type NewSubscriptionFlowEvents =
  | AddPetNameEvent
  | AddPetInfosEvent
  | AddPetBreedEvent
  | AddPetBirthDateEvent
  | SetQuoteFromBackendEvent
  | AddAttributionQuestionEvent
  | AddOwnerFirstInformations
  | UpdateQuoteEvent
  | UpdateCurrentPetIdx
  | SetCurrentPetIdx
  | AddNewPet
  | StopLoadingPrepackage
  | StartLoadingPrepackage
  | SetStripeClientSecretEvent
  | SetEmailSentToEvent

export const newSubscriptionFlowMachine = createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QDswHcC0sCuAjWAxgE4CWADgC4kD2yGAZgDbVoB0JEjYAxAIIAi-APoAFAKIAVIQDleAWTEBtAAwBdRKDLVYJKrQ0gAHogCMJgKysALACYAnADY7ym7bvmzNgDQgAnogB2cwdWBwCTAA4TK2VzALsIuIBfJJ9UTBx8YnI9OiYWdk4eAWFxKQBJaQAxAHkAJTleCXKa6QBlFXUkEC0dXINjBAjlVhtzAGZnJ3M7II8TH38EBwcRm0inIICA5QCrcxS09Cw8QlJKGjzmNg4uPkFRSSEAITqxMX5Og17dS4HTEzjawmZQWOxWBwmBxWCJ2caLRDBIEBBzmcxRZSOKHjA6pEDpE5Zc65BjXQp3EqPKTPcp1CQACSE-CaSjU320v303UGdhsrEiON5GNm6zsCIQEICrHGJlc0Mh5jGrkO+OOmTOOUupIKtx4bSeAEUAKo1CRiIRVOo1OQvXgAYQA0mJpJ82d0fv1uYhUaEwiKceNwg5xt4-IhAyNEnCYYCrOMrICVQT1dkLrRtTcivdhE0JHVys8jc1WkJjWI2sXpF93RzPaBBg4+SYthErHCAnKUeKbHtrMpGzLUfHxi4Akm1adUyT8pmKQ8agB1aRiOoW2kVoSVWoNJotdrVzS1v5eiWhpbNqXuGUWZtjEy88bjjKT4lamfknhGkTMs2lk1mg8eiPLl6wBSxxkFHE9nRSEz0QKJrDsJCEicKFlAiGwUjxZBqAgOADGTF9NXTGd2T6Y9QIQDAHHFDBLGQhjGKQx9sInIliKuHUijIzlkH+BAoRMVhwQmDZFXGWEAnFRIIn5Vw4ViCTYTCLCkiAA */
  tsTypes: {} as import("./machine.typegen").Typegen0,
  predictableActionArguments: true,
  schema: {
    context: {} as NewSubscriptionFlowContext,
    events: {} as NewSubscriptionFlowEvents,
  },
  id: "new-subscription-flow",
  context: {
    quote: {
      pets: [],
    },
    currentPetIdx: 0,
    currentPetId: null,
    nextPetIndex: 0,
    setLoadingPrepackage: false,
    stripeClientSecret: null,
  },
  initial: "idle",
  states: {
    idle: {
      on: {
        ADD_PET_NAME: {
          actions: assign<NewSubscriptionFlowContext, AddPetNameEvent>(
            (context, { petName, petId }) => {
              const petIndexToEdit = getPetIndex(context.quote.pets || [], petId)
              if (context.quote?.pets?.[petIndexToEdit]) {
                ;(context.quote.pets[petIndexToEdit] || {}).name = petName
              } else {
                const newPetId = context.currentPetId || generatePetId()
                context.quote.pets = [
                  ...(context.quote.pets || []),
                  {
                    name: petName,
                    idx: context.nextPetIndex,
                    id: newPetId,
                  },
                ]
                context.nextPetIndex = context.nextPetIndex + 1
                context.currentPetId = newPetId
              }
            }
          ),
        },
        UPDATE_CURRENT_PET_IDX: {
          actions: assign<NewSubscriptionFlowContext, UpdateCurrentPetIdx>(
            (context, { petIndex, petId }) => {
              if (petId) {
                context.currentPetId = petId
              }
              context.currentPetIdx = petIndex
            }
          ),
        },
        ADD_PET_INFORMATIONS: {
          actions: assign<NewSubscriptionFlowContext, AddPetInfosEvent>(
            (context, { petGender, petType, petId }) => {
              const petIndexToEdit = getPetIndex(context.quote.pets || [], petId)
              if (context.quote && context.quote.pets && context.quote.pets[petIndexToEdit]) {
                context.quote.pets[petIndexToEdit].sexe = petGender
                context.quote.pets[petIndexToEdit].type = petType
              }
            }
          ),
        },
        ADD_PET_BREED: {
          actions: assign<NewSubscriptionFlowContext, AddPetBreedEvent>(
            (context, { petBreed, petIndex }) => {
              if (context.quote.pets?.[petIndex]) {
                context.quote.pets[petIndex].breed = petBreed
              }
            }
          ),
        },
        ADD_PET_BIRTH_DATE: {
          actions: assign<NewSubscriptionFlowContext, AddPetBirthDateEvent>(
            (context, { petBirthDate, petIndex }) => {
              if (context.quote.pets?.[petIndex]) {
                context.quote.pets[petIndex].birthday = petBirthDate
              }
            }
          ),
        },
        SET_QUOTE_FROM_BACKEND: {
          actions: assign<NewSubscriptionFlowContext, SetQuoteFromBackendEvent>(
            (context, { quoteResponse }) => {
              if (quoteResponse[0]?.quote_id) {
                context.quote.meta = {
                  ...(context.quote?.meta || {}),
                  quote_id: quoteResponse[0]?.quote_id,
                  user_id: generateUserId(),
                }
              }
              if (R.isNotNil(context.currentPetId)) {
                const petIndexToEdit = getPetIndex(context.quote.pets || [], context.currentPetId)
                if (context.quote?.pets?.[petIndexToEdit] && quoteResponse[0]?.created_at) {
                  context.quote.pets[petIndexToEdit].created_at = quoteResponse[0]?.created_at
                }
              }
            }
          ),
        },
        ADD_ATTRIBUTION_QUESTION: {
          actions: assign<NewSubscriptionFlowContext, AddAttributionQuestionEvent>(
            (context, event) => {
              context.quote.marketing = {
                ...(context.quote.marketing || {}),
                referrer_details: event.referrer_details,
                referrer_type: event.referrer_type,
                shop: event.shop || undefined,
              }
            }
          ),
        },
        ADD_OWNER_FIRST_INFORMATIONS: {
          actions: assign<NewSubscriptionFlowContext, AddOwnerFirstInformations>(
            (context, event) => {
              context.quote.owner = {
                ...(context.quote.owner || {}),
                email: event.email,
              }
              context.pricing_version = event.pricing_version
              context.rc_contract_id = event.rc_contract_id
              context.risk_carrier = event.risk_carrier
            }
          ),
        },
        UPDATE_QUOTE: {
          actions: assign<NewSubscriptionFlowContext, UpdateQuoteEvent>((context, event) => {
            context.quote = event.quote
          }),
        },
        SET_CURRENT_PET_IDX: {
          actions: assign<NewSubscriptionFlowContext, SetCurrentPetIdx>((context, event) => {
            context.currentPetIdx = event.petIndex
            context.currentPetId = event.petId
          }),
        },
        ADD_NEW_PET: {
          actions: assign<NewSubscriptionFlowContext, AddNewPet>((context) => {
            const petIndexes = R.map((pet) => pet.idx, context.quote.pets || [])
            // @ts-expect-error this is an expected error as id isn't in backend type
            const maxIndex: number = R.reduce(R.maxBy(R.identity), 0, petIndexes)
            context.currentPetIdx = (maxIndex || -1) + 1
          }),
        },
        STOP_LOADING_PREPACKAGE: {
          actions: assign<NewSubscriptionFlowContext, StopLoadingPrepackage>((context) => {
            context.setLoadingPrepackage = false
          }),
        },
        START_LOADING_PREPACKAGE: {
          actions: assign<NewSubscriptionFlowContext, StartLoadingPrepackage>((context) => {
            context.setLoadingPrepackage = true
          }),
        },
        SET_STRIPE_CLIENT_SECRET: {
          actions: assign<NewSubscriptionFlowContext, SetStripeClientSecretEvent>(
            (context, event) => {
              context.stripeClientSecret = event.clientSecret
            }
          ),
        },
        SET_EMAIL_SENT_TO: {
          actions: assign<NewSubscriptionFlowContext, SetEmailSentToEvent>((context, event) => {
            context.quote.owner = { ...(context.quote.owner || {}), email: event.email }
          }),
        },
      },
    },
  },
})

export const newSubscriptionFlowService = interpret(newSubscriptionFlowMachine, {
  devTools: true,
}).start()
